import React from 'react'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Portlet, Table } from '_zari/core'

import { DownloadAxiosFile, ZrSwal } from 'utils'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import TiposDeDocumentoSunatConstant from 'constants/tiposDeDocumentoSunat'

import * as FacturacionService from 'services/facturacion.service'
import * as DocumentoRelacionadoService from 'services/documentoRelacionado.service'
import * as Modals from './modals'


const portletBreadcrumbs = [{ name: 'Ventas' }]

const tableHeaderRows = [
  {
    label: 'FECHA',
    width: 85,
    align: 'center',
    filter: {
      type: 'text',
      name: 'fechaEmision'
    }
  },
  { label: 'TIPO', width: 90, align: 'center' },
  {
    label: 'SERIE DOC',
    width: 105,
    align: 'center',
    filter: {
      type: 'text',
      name: 'serDoc'
    }
  },
  {
    label: 'NUM DOC',
    width: 105,
    align: 'center',
    filter: {
      type: 'text',
      name: 'numDoc'
    }
  },
  {
    label: 'RUC',
    width: 100,
    align: 'center',
    filter: {
      type: 'text',
      name: 'ruc'
    }
  },
  {
    label: 'RAZÓN SOCIAL',
    align: 'center',
    filter: {
      type: 'text',
      name: 'razSoc'
    }
  },
  {
    label: 'USUARIO',
    align: 'center',
    width: 100
  },
  { label: '', width: 30, align: 'center' }
]

const ListaVentasPage = ({ history, location }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const [mostrarPreviewPdfModal, setMostrarPreviewPdfModal] = React.useState(false)
  const [idPDFPreview, setIdPDFPreview] = React.useState(null)

  const tablaRef = React.useRef(null)

  const flagSinEntrega = React.useMemo(() => {
    return location.pathname === `/${CodigosTransaccionesConstant.VENTAS_SINENTREGA_LISTA}`
  }, [location.pathname])

  const flagVentaRapida = React.useMemo(() => {
    return location.pathname === `/${CodigosTransaccionesConstant.VENTASRAPIDAS_LISTA}`
  }, [location.pathname])

  const clickNuevoHandler = React.useCallback(() => {
    if (flagSinEntrega) {
      history.push(`/${CodigosTransaccionesConstant.VENTAS_SINENTREGA_NUEVA}`)
    } else if (flagVentaRapida) {
      history.push(`/${CodigosTransaccionesConstant.VENTASRAPIDAS_NUEVO}`)
    } else {
      history.push(`/${CodigosTransaccionesConstant.VENTAS_NUEVA}`)
    }
  }, [flagVentaRapida, flagSinEntrega, history])

  const clickEditarHandler = React.useCallback(
    ({ _id }) => {
      if (flagSinEntrega) {
        history.push(`/${CodigosTransaccionesConstant.VENTAS_SINENTREGA_NUEVA}/${_id}`)
      } else if (flagVentaRapida) {
        history.push(`/${CodigosTransaccionesConstant.VENTASRAPIDAS_NUEVO}/${_id}`)
      } else {
        history.push(`/${CodigosTransaccionesConstant.VENTAS_NUEVA}/${_id}`)
      }
    },
    [flagVentaRapida, flagSinEntrega, history]
  )

  const clickGenerarNotaCredito = React.useCallback(
    async ({ _id }) => {
      await DocumentoRelacionadoService.obtenerDocumentosxIdDocumentoPrincipal(_id)
        .then(({ data }) => {
          if (data.length) {
            ZrSwal.showAlert({
              tipo: 'error',
              mensaje: 'Se han encontrado documentos relacionados a este documento.',
              onSuccess: () => {}
            })
          } else {
            if (flagSinEntrega) {
              history.push(
                `/${CodigosTransaccionesConstant.VENTAS_SINENTREGA_NUEVA}/${_id}/${TiposDeDocumentoSunatConstant.NOTA_CREDITO}`
              )
            } else {
              history.push(
                `/${CodigosTransaccionesConstant.VENTAS_NUEVA}/${_id}/${TiposDeDocumentoSunatConstant.NOTA_CREDITO}`
              )
            }
          }
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
        })
    },
    [flagSinEntrega, history, __zariContext]
  )

  const clickEditarComentariosHandler = React.useCallback(
    async ({ _id }) => {
      history.push(`/${CodigosTransaccionesConstant.VENTAS_NUEVA}/${_id}?flagEdicion=true`)
    },
    [history]
  )

  const clickPreviewPdfHandler = React.useCallback(async ({ _id }) => {
    setIdPDFPreview(_id)
    setMostrarPreviewPdfModal(true)
  }, [])

  const clickImprimirHandler = React.useCallback(
    async ({ _id }) => {
      await FacturacionService.descargarPdfA4xId(_id)
        .then(({ data, headers }) => {
          DownloadAxiosFile.downloadAsBlob(data, headers)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickImprimirTicketHandler = React.useCallback(
    async ({ _id }) => {
      await FacturacionService.descargarPdfTicketxId(_id)
        .then(({ data, headers }) => {
          DownloadAxiosFile.downloadAsBlob(data, headers)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickRegularizarFactura = React.useCallback(
    async ({ _id }) => {
      __zariContext.loader.open('Generando Asiento Contable...')
      await FacturacionService.regularizarAsientoxId(_id)
        .then(() => {
          __zariContext.snack('success', 'Asiento Generado Correctamente')
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
        .finally(() => {
          __zariContext.loader.close()
        })
    },
    [__zariContext]
  )

  const clickAnularFactura = React.useCallback(
    async ({ _id }) => {
      __zariContext.loader.open('Cargando Factura...')
      await FacturacionService.obtenerInfoAnulacion(_id)
        .then(() => {
          history.push(`/${CodigosTransaccionesConstant.VENTAS_NUEVA}/${_id}`, { flagAnular: true })
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
        .finally(() => {
          __zariContext.loader.close()
        })
    },
    [history, __zariContext]
  )

  const clickRegularizarTodo = React.useCallback(() => {
    __zariContext.loader.open('Regularizando las ventas de esta sede...')
    FacturacionService.regularizarxCompaniaxSede()
      .then(() => {
        __zariContext.snack('success', 'Los asientos han sido regularizados Correctamente')
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
      .finally(() => {
        __zariContext.loader.close()
      })
  }, [__zariContext])

  return (
    <>
      <Portlet key={flagSinEntrega ? 'Ventas sin Entrega' : flagVentaRapida ? 'Ventas rapidas' : 'Ventas'}>
        <Portlet.Header
          title={flagSinEntrega ? 'Ventas sin Entrega' : flagVentaRapida ? 'Ventas rapidas' : 'Ventas'}
          breadcrumbs={portletBreadcrumbs}
          buttons={[
            { name: 'Nuevo', onClick: clickNuevoHandler },
            { name: 'Regularizar Todo', onClick: clickRegularizarTodo }
          ]}
        />
        <Portlet.Body>
          <Table.ServerSide
            ref={tablaRef}
            useFilters={true}
            useNest={true}
            url='/facturaciones'
            baseParams={{
              flagSinEntrega: flagSinEntrega,
              flagVentaRapida: flagVentaRapida
            }}
            headRows={tableHeaderRows}
            rowTemplate={row => (
              <Table.Row hover key={row._id}>
                <Table.Cell align='center'>{row.fechaEmision || '--'}</Table.Cell>
                <Table.Cell align='center'>
                  {row.tipoDocumento ? TiposDeDocumentoSunatConstant.__dictionary[row.tipoDocumento].nombre : '--'}
                </Table.Cell>
                <Table.Cell align='center'>{row.serieDocumento || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.numeroDocumento || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.documentoSocio || '--'}</Table.Cell>
                <Table.Cell>{row.nombreSocio || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.usuario || '--'}</Table.Cell>
                <Table.Cell>
                  <Table.Dropdown>
                    {({ handleClose }) => [
                      ...(!__adminContext.valoresConfiguracionUsuario.flagEditarVentas
                        ? []
                        : [
                            <Table.Dropdown.Item
                              key='editar'
                              handleClose={handleClose}
                              onClick={e => clickEditarHandler({ _id: row._id })}
                            >
                              Editar
                            </Table.Dropdown.Item>
                          ]),
                      ...(flagSinEntrega ||
                      flagVentaRapida ||
                      !__adminContext.valoresConfiguracionUsuario.flagEditarVentas
                        ? []
                        : [
                            <Table.Dropdown.Item
                              key='editar-comentarios'
                              handleClose={handleClose}
                              onClick={e => clickEditarComentariosHandler({ _id: row._id })}
                            >
                              Editar Comentario
                            </Table.Dropdown.Item>
                          ]),
                      <Table.Dropdown.Divider key='divider-1' />,
                      <Table.Dropdown.Item
                        key='preview-pdf'
                        handleClose={handleClose}
                        onClick={() => clickPreviewPdfHandler({ _id: row._id })}
                      >
                        Previsualizar A4
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='imprimir-pdf'
                        handleClose={handleClose}
                        onClick={() => clickImprimirHandler({ _id: row._id })}
                      >
                        Imprimir A4
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='regularizar-factura'
                        handleClose={handleClose}
                        onClick={() => clickRegularizarFactura({ _id: row._id })}
                      >
                        Regularizar Factura
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='anular-factura'
                        handleClose={handleClose}
                        onClick={() => clickAnularFactura({ _id: row._id })}
                      >
                        Anular Factura
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='generar-nota-credito'
                        handleClose={handleClose}
                        onClick={e => clickGenerarNotaCredito({ _id: row._id })}
                      >
                        Generar Nota Credito
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='imprimir-ticket'
                        handleClose={handleClose}
                        onClick={() => clickImprimirTicketHandler({ _id: row._id })}
                      >
                        Imprimir Ticket
                      </Table.Dropdown.Item>
                    ]}
                  </Table.Dropdown>
                </Table.Cell>
              </Table.Row>
            )}
          />
        </Portlet.Body>
      </Portlet>
      {mostrarPreviewPdfModal && idPDFPreview && (
        <Modals.PreviewPdfModal
          handleClose={() => setMostrarPreviewPdfModal(false)}
          show={mostrarPreviewPdfModal}
          pdfHash={idPDFPreview}
        />
      )}
    </>
  )
}

export default ListaVentasPage
